import { useState, useEffect } from "react";
import {
  Plus, Trash2, RefreshCw, CheckCircle, AlertTriangle,
  Clock, Zap, Lock, ChevronDown, ChevronUp, X
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  SiSlack, SiGithub, SiJira, SiLinear, SiNotion,
  SiConfluence, SiGmail, SiHubspot, SiPagerduty, SiZapier
} from "react-icons/si";

const CONNECTOR_DEFS = [
  {
    type: "slack",
    Icon: SiSlack,
    color: "text-[#4A154B]",
    bg: "bg-[#4A154B]/10",
    border: "border-[#4A154B]/30",
    label: "Slack",
    description: "Learn from channel conversations, decisions, and threads",
    fields: [
      { key: "bot_token", label: "Bot Token", placeholder: "xoxb-...", secret: true },
      { key: "channels", label: "Channels to watch", placeholder: "general,engineering,incidents", secret: false },
    ],
  },
  {
    type: "github",
    Icon: SiGithub,
    color: "text-ink-100",
    bg: "bg-ink-100/10",
    border: "border-ink-100/20",
    label: "GitHub",
    description: "Index PRs, issues, code reviews, and post-mortems",
    fields: [
      { key: "access_token", label: "Personal Access Token", placeholder: "ghp_...", secret: true },
      { key: "repos", label: "Repositories", placeholder: "org/repo1,org/repo2", secret: false },
    ],
  },
  {
    type: "jira",
    Icon: SiJira,
    color: "text-[#0052CC]",
    bg: "bg-[#0052CC]/10",
    border: "border-[#0052CC]/30",
    label: "Jira",
    description: "Extract patterns from tickets, resolutions, and sprints",
    fields: [
      { key: "api_token", label: "API Token", placeholder: "ATATT...", secret: true },
      { key: "email", label: "Account Email", placeholder: "you@company.com", secret: false },
      { key: "domain", label: "Domain", placeholder: "yourcompany.atlassian.net", secret: false },
      { key: "project_keys", label: "Project Keys", placeholder: "ENG,OPS,INFRA", secret: false },
    ],
  },
  {
    type: "linear",
    Icon: SiLinear,
    color: "text-[#5E6AD2]",
    bg: "bg-[#5E6AD2]/10",
    border: "border-[#5E6AD2]/30",
    label: "Linear",
    description: "Learn from issues, cycles, and engineering decisions",
    fields: [
      { key: "api_key", label: "API Key", placeholder: "lin_api_...", secret: true },
      { key: "team_ids", label: "Team IDs (optional)", placeholder: "Leave blank for all teams", secret: false },
    ],
  },
  {
    type: "notion",
    Icon: SiNotion,
    color: "text-ink-100",
    bg: "bg-ink-100/10",
    border: "border-ink-100/20",
    label: "Notion",
    description: "Ingest playbooks, runbooks, and SOPs",
    fields: [
      { key: "api_key", label: "Integration Token", placeholder: "secret_...", secret: true },
      { key: "database_ids", label: "Database IDs (optional)", placeholder: "Leave blank to index all", secret: false },
    ],
  },
  {
    type: "confluence",
    Icon: SiConfluence,
    color: "text-[#0052CC]",
    bg: "bg-[#0052CC]/10",
    border: "border-[#0052CC]/30",
    label: "Confluence",
    description: "Learn from internal wikis and documentation",
    comingSoon: true,
    fields: [],
  },
  {
    type: "gmail",
    Icon: SiGmail,
    color: "text-[#EA4335]",
    bg: "bg-[#EA4335]/10",
    border: "border-[#EA4335]/30",
    label: "Gmail",
    description: "Learn from client communications and escalations",
    comingSoon: true,
    fields: [],
  },
  {
    type: "hubspot",
    Icon: SiHubspot,
    color: "text-[#FF7A59]",
    bg: "bg-[#FF7A59]/10",
    border: "border-[#FF7A59]/30",
    label: "HubSpot",
    description: "Extract patterns from deals, contacts, and sales workflows",
    fields: [
      { key: "access_token", label: "Private App Token", placeholder: "pat-...", secret: true },
      { key: "objects", label: "Objects to sync", placeholder: "deals,contacts,tickets", secret: false },
    ],
  },
  {
    type: "pagerduty",
    Icon: SiPagerduty,
    color: "text-[#06AC38]",
    bg: "bg-[#06AC38]/10",
    border: "border-[#06AC38]/30",
    label: "PagerDuty",
    description: "Learn from incidents, alerts, and post-mortems",
    fields: [
      { key: "api_token", label: "API Token", placeholder: "u+...", secret: true },
      { key: "service_ids", label: "Service IDs (optional)", placeholder: "Leave blank for all", secret: false },
    ],
  },
  {
    type: "zapier",
    Icon: SiZapier,
    color: "text-[#FF4A00]",
    bg: "bg-[#FF4A00]/10",
    border: "border-[#FF4A00]/30",
    label: "Zapier",
    description: "Send data from any tool via Zapier webhook",
    fields: [
      { key: "webhook_secret", label: "Webhook Secret (auto-generated)", placeholder: "Will be generated on save", secret: true },
    ],
  },
];

function StatusBadge({ status }) {
  const map = {
    connected: { cls: "bg-terminal/20 text-terminal", label: "Connected" },
    syncing:   { cls: "bg-amber/20 text-amber animate-pulse", label: "Syncing" },
    error:     { cls: "bg-hotpink/20 text-hotpink", label: "Error" },
    disconnected: { cls: "bg-line-2 text-ink-500", label: "Disconnected" },
  };
  const s = map[status] || map.disconnected;
  return (
    <span className={`text-[10px] px-2 py-0.5 uppercase tracking-widest ${s.cls}`}>
      {s.label}
    </span>
  );
}

function ConnectorCard({ def, connected, onConnect, onDisconnect, onSync }) {
  const [expanded, setExpanded] = useState(false);
  const [fields, setFields] = useState({});
  const [name, setName] = useState(def.label);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const isConnected = !!connected;

  const handleSave = async () => {
    const missing = def.fields.filter(f => f.secret && !fields[f.key]);
    if (missing.length && !isConnected) {
      toast.error(`Fill in: ${missing.map(f => f.label).join(", ")}`);
      return;
    }
    setSaving(true);
    await onConnect(def.type, name, fields);
    setSaving(false);
    setExpanded(false);
    setFields({});
  };

  const handleSync = async () => {
    setSyncing(true);
    await onSync(connected.id);
    setSyncing(false);
  };

  return (
    <div className={`border ${isConnected ? "border-terminal/30" : "border-line-1"} bg-surface transition-colors`}>
      {/* Header */}
      <div className="p-5 flex items-center gap-4">
        <div className={`w-10 h-10 flex items-center justify-center ${def.bg} border ${def.border} shrink-0`}>
          <def.Icon size={20} className={def.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-sm uppercase tracking-tight">{def.label}</span>
            {isConnected && <StatusBadge status={connected.status} />}
            <span className="flex items-center gap-1 text-[10px] text-terminal tracking-wider">
              <Lock size={9} /> private
            </span>
          </div>
          <p className="text-xs text-ink-500 mt-0.5 truncate">{def.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isConnected && (
            <>
              <button
                onClick={handleSync}
                disabled={syncing || connected.status === "syncing"}
                className="p-1.5 text-ink-500 hover:text-cyber transition-colors disabled:opacity-40"
                title="Sync now"
              >
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
              </button>
              <button
                onClick={() => onDisconnect(connected.id)}
                className="p-1.5 text-ink-500 hover:text-hotpink transition-colors"
                title="Disconnect"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
          {!isConnected && !def.comingSoon && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase border border-cyber/40 text-cyber px-3 py-1.5 hover:bg-cyber/10 transition-colors"
            >
              <Plus size={12} /> Connect
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
          {def.comingSoon && (
            <span className="text-[10px] tracking-[0.2em] uppercase border border-line-2 text-ink-500 px-3 py-1.5">
              Coming Soon
            </span>
          )}
          {isConnected && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 text-ink-500 hover:text-ink-100 transition-colors"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>

      {/* Stats row for connected */}
      {isConnected && !expanded && (
        <div className="px-5 pb-4 flex gap-6 text-xs text-ink-500">
          <span className="flex items-center gap-1">
            <Zap size={11} className="text-cyber" />
            {connected.events_ingested.toLocaleString()} events ingested
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle size={11} className="text-terminal" />
            {connected.lessons_created.toLocaleString()} lessons created
          </span>
          {connected.last_sync_at && (
            <span className="flex items-center gap-1">
              <Clock size={11} />
              Last sync {new Date(connected.last_sync_at).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {/* Expand form */}
      {expanded && (
        <div className="border-t border-line-1 p-5 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-ink-500 mb-1.5">
              Connector name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-void border border-line-2 px-3 py-2 text-sm text-ink-100 focus:border-cyber focus:outline-none"
            />
          </div>
          {def.fields.map(f => (
            <div key={f.key}>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-ink-500 mb-1.5">
                {f.label}
              </label>
              <input
                type={f.secret ? "password" : "text"}
                value={fields[f.key] || ""}
                onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full bg-void border border-line-2 px-3 py-2 text-sm text-ink-100 focus:border-cyber focus:outline-none placeholder:text-ink-600"
              />
            </div>
          ))}
          {connected?.last_error && (
            <div className="flex items-start gap-2 text-xs text-hotpink bg-hotpink/5 border border-hotpink/20 p-3">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              {connected.last_error}
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-cyber text-void px-4 py-2 text-xs font-bold tracking-wider uppercase disabled:opacity-40 hover:shadow-[0_0_12px_rgba(0,229,255,0.3)] transition-shadow"
            >
              {saving ? "Saving…" : isConnected ? "Update" : "Connect"}
            </button>
            <button
              onClick={() => { setExpanded(false); setFields({}); }}
              className="flex-1 border border-line-2 text-ink-300 px-4 py-2 text-xs font-bold tracking-wider uppercase hover:border-line-1 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConnectorsView() {
  const { apiFetch } = useAuth();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConnectors = async () => {
    try {
      const res = await apiFetch("/connectors");
      if (res.ok) setConnectors(await res.json());
    } catch (e) {
      if (e.message !== "Session expired. Please log in again.") console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConnectors(); }, []);

  const handleConnect = async (type, name, credentials) => {
    try {
      const res = await apiFetch("/connectors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, name, credentials }),
      });
      if (res.ok) {
        toast.success(`${name} connected. First sync starting…`);
        fetchConnectors();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to connect");
      }
    } catch (e) {
      if (e.message !== "Session expired. Please log in again.") toast.error("Connection failed");
    }
  };

  const handleDisconnect = async (id) => {
    try {
      const res = await apiFetch(`/connectors/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Connector removed");
        fetchConnectors();
      }
    } catch (e) {
      if (e.message !== "Session expired. Please log in again.") toast.error("Failed to disconnect");
    }
  };

  const handleSync = async (id) => {
    try {
      const res = await apiFetch(`/connectors/${id}/sync`, { method: "POST" });
      if (res.ok) {
        toast.success("Sync triggered");
        fetchConnectors();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Sync failed");
      }
    } catch (e) {
      if (e.message !== "Session expired. Please log in again.") toast.error("Sync failed");
    }
  };

  const connectedMap = Object.fromEntries(connectors.map(c => [c.type, c]));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
          / control.room / connectors
        </div>
        <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">
          Data <span className="text-cyber">Connectors</span>
        </h1>
        <p className="text-ink-500 text-sm mt-2 max-w-xl">
          Connect your tools and Mnemonic will passively learn your company's workflows,
          playbooks, and decisions. All lessons stay{" "}
          <span className="text-terminal">private to your workspace</span> — never shared globally.
        </p>
      </div>

      {/* Privacy notice */}
      <div className="border border-terminal/20 bg-terminal/5 p-4 flex items-start gap-3">
        <Lock size={16} className="text-terminal mt-0.5 shrink-0" />
        <div className="text-xs text-ink-400 leading-relaxed">
          <span className="text-terminal font-semibold">All connector data is private.</span>{" "}
          Lessons extracted from your connected tools are visible only to your agents.
          They are never contributed to the global knowledge pool.
        </div>
      </div>

      {/* Connector cards */}
      {loading ? (
        <div className="text-center py-12 text-ink-500 text-[10px] tracking-[0.3em] uppercase animate-pulse">
          Loading…
        </div>
      ) : (
        <div className="space-y-3">
          {CONNECTOR_DEFS.map(def => (
            <ConnectorCard
              key={def.type}
              def={def}
              connected={connectedMap[def.type] || null}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onSync={handleSync}
            />
          ))}
        </div>
      )}
    </div>
  );
}
